import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import siteUrl from '../../../../const/const';
import {
  AppealHandout,
  AppealQuestion,
  AppealQuestionInput,
  AppealSession,
  HandoutKind,
  MAX_HANDOUT_IMAGE_BYTES,
  QuestionKind,
  uploadHandout,
} from '../../../../services/appealsService';
import { ErrorText, fieldCls, labelCls, primaryBtnCls, secondaryBtnCls } from './shared';

const PART_COUNT: Record<QuestionKind, number> = { single: 1, duplet: 2, blitz: 3 };
const PART_LABELS = ['1-ci sual', '2-ci sual', '3-cü sual'];

const emptyInput = (number: number): AppealQuestionInput => ({
  number,
  kind: 'single',
  isTranslated: false,
  noteBefore: '',
  sharedHandout: null,
  parts: [{ text: '', answer: '', handout: null }],
  considered: '',
  comment: '',
  sources: [],
  authors: '',
});

const toInput = ({ id, code, createdAt, ...rest }: AppealQuestion): AppealQuestionInput => rest;

const newHandout = (kind: HandoutKind = 'text'): AppealHandout => ({ kind, text: '', image: '', url: '' });

const Segmented = <T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) => (
  <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 p-1">
    {options.map(o => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        aria-pressed={value === o.value}
        className={cn(
          'rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
          value === o.value
            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }> = ({
  checked,
  onChange,
  children,
}) => (
  <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
    />
    {children}
  </label>
);

/** One handout: text, or a photo uploaded straight to the room. Mirrors the admin "Rekvizit" fields. */
const HandoutEditor: React.FC<{
  session: AppealSession;
  value: AppealHandout;
  onChange: (value: AppealHandout) => void;
  onError: (message: string) => void;
}> = ({ session, value, onChange, onError }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) return onError('Yalnız PNG və ya JPG şəkil yükləyin.');
    if (file.size > MAX_HANDOUT_IMAGE_BYTES) return onError('Şəkil 2 MB-dan böyük olmamalıdır.');
    onError('');
    setUploading(true);
    try {
      const uploaded = await uploadHandout(session, file);
      onChange({ ...value, image: uploaded.image, url: uploaded.url });
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Şəkil yüklənmədi.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Segmented<HandoutKind>
        value={value.kind}
        onChange={kind => onChange({ ...value, kind })}
        options={[
          { value: 'text', label: 'Rekvizit mətn' },
          { value: 'image', label: 'Rekvizit foto' },
        ]}
      />
      {value.kind === 'text' ? (
        <textarea
          rows={3}
          value={value.text}
          onChange={e => onChange({ ...value, text: e.target.value })}
          placeholder="Rekvizitin mətnini daxil edin"
          className={fieldCls}
        />
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".png,.jpg,.jpeg"
            className="hidden"
            onChange={e => {
              pickImage(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          {value.image ? (
            <div className="relative inline-block">
              <img
                src={`${siteUrl}images/${value.url}`}
                alt="Rekvizit"
                className="max-h-56 max-w-full rounded-xl border border-gray-200 dark:border-gray-700"
              />
              <button
                type="button"
                onClick={() => onChange({ ...value, image: '', url: '' })}
                aria-label="Şəkli sil"
                className="absolute right-2 top-2 rounded-full bg-gray-900/70 p-1 text-white hover:bg-gray-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 px-4 py-8 text-sm font-medium text-gray-500 dark:text-gray-400 transition-colors hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-700 dark:hover:text-indigo-300"
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
              {uploading ? 'Yüklənir…' : 'Şəkil yüklə (PNG / JPG, maks. 2 MB)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface Props {
  session: AppealSession;
  initial?: AppealQuestion;
  nextNumber: number;
  onSubmit: (input: AppealQuestionInput) => Promise<void>;
  onCancel: () => void;
}

/** The admin NHN (Type 1) question form, for appeal rooms. */
const AppealQuestionForm: React.FC<Props> = ({ session, initial, nextNumber, onSubmit, onCancel }) => {
  const [form, setForm] = useState<AppealQuestionInput>(() => (initial ? toInput(initial) : emptyInput(nextNumber)));
  const [sourcesText, setSourcesText] = useState(() => (initial ? initial.sources.join('\n') : ''));
  const [hasNote, setHasNote] = useState(Boolean(initial?.noteBefore));
  // "Rekvizit var" and, for duplet/blitz, "Hamısı üçün" — same switches as the admin form
  const [hasHandout, setHasHandout] = useState(
    Boolean(initial && (initial.sharedHandout || initial.parts.some(p => p.handout)))
  );
  const [forAll, setForAll] = useState(Boolean(initial?.sharedHandout));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const multi = form.parts.length > 1;

  const set = <K extends keyof AppealQuestionInput>(key: K, value: AppealQuestionInput[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const setKind = (kind: QuestionKind) => {
    setForm(prev => {
      const count = PART_COUNT[kind];
      const parts = Array.from({ length: count }, (_, i) => prev.parts[i] ?? { text: '', answer: '', handout: null });
      return { ...prev, kind, parts };
    });
    if (kind === 'single') setForAll(false);
  };

  const setPart = (index: number, change: Partial<AppealQuestionInput['parts'][number]>) =>
    setForm(prev => ({
      ...prev,
      parts: prev.parts.map((p, i) => (i === index ? { ...p, ...change } : p)),
    }));

  const toggleHandout = (on: boolean) => {
    setHasHandout(on);
    if (on && !multi && !form.parts[0].handout) setPart(0, { handout: newHandout() });
  };

  const toggleForAll = (on: boolean) => {
    setForAll(on);
    if (on && !form.sharedHandout) set('sharedHandout', newHandout());
  };

  const handoutError = (h: AppealHandout | null) => {
    if (!h) return '';
    if (h.kind === 'text' && !h.text.trim()) return 'Rekvizitin mətnini daxil edin.';
    if (h.kind === 'image' && !h.image) return 'Rekvizitin şəklini yükləyin.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const useShared = hasHandout && multi && forAll;
    const input: AppealQuestionInput = {
      ...form,
      noteBefore: hasNote ? form.noteBefore.trim() : '',
      sharedHandout: useShared ? form.sharedHandout : null,
      parts: form.parts.map(p => ({ ...p, handout: hasHandout && !useShared ? p.handout : null })),
      sources: sourcesText.split('\n').map(s => s.trim()).filter(Boolean),
    };

    if (!input.number || input.number < 1) return setError('Sualın nömrəsini daxil edin.');
    if (input.parts.some(p => !p.text.trim())) return setError('Sualın mətnini daxil edin.');
    if (input.parts.some(p => !p.answer.trim())) return setError('Sualın cavabını daxil edin.');
    if (hasHandout && !useShared && input.parts.every(p => !p.handout))
      return setError('Rekvizit əlavə edin və ya "Rekvizit var" seçimini söndürün.');
    const handoutProblem = [input.sharedHandout, ...input.parts.map(p => p.handout)].map(handoutError).find(Boolean);
    if (handoutProblem) return setError(handoutProblem);

    setError('');
    setSaving(true);
    try {
      await onSubmit(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sual saxlanılmadı.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-28">
          <label htmlFor="aq-number" className={labelCls}>Sual nömrəsi</label>
          <input
            id="aq-number"
            type="number"
            min={1}
            value={form.number || ''}
            onChange={e => set('number', Number(e.target.value))}
            className={fieldCls}
          />
        </div>
        <div>
          <span className={labelCls}>Sualın növü</span>
          <Segmented
            value={form.kind}
            onChange={setKind}
            options={[
              { value: 'single', label: 'Adi' },
              { value: 'duplet', label: 'Duplet' },
              { value: 'blitz', label: 'Blits' },
            ]}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Toggle checked={form.isTranslated} onChange={v => set('isTranslated', v)}>Sual tərcümədir</Toggle>
        <Toggle checked={hasNote} onChange={setHasNote}>Qeyd var</Toggle>
        <Toggle checked={hasHandout} onChange={toggleHandout}>Rekvizit var</Toggle>
        {hasHandout && multi && (
          <Toggle checked={forAll} onChange={toggleForAll}>Rekvizit hamısı üçün</Toggle>
        )}
      </div>

      {hasNote && (
        <textarea
          rows={2}
          value={form.noteBefore}
          onChange={e => set('noteBefore', e.target.value)}
          placeholder="Sualdan əvvəl oxunan qeyd"
          className={fieldCls}
        />
      )}

      {hasHandout && multi && forAll && form.sharedHandout && (
        <div className="space-y-1">
          <span className={labelCls}>Ortaq rekvizit</span>
          <HandoutEditor
            session={session}
            value={form.sharedHandout}
            onChange={v => set('sharedHandout', v)}
            onError={setError}
          />
        </div>
      )}

      <div className="space-y-4">
        {form.parts.map((part, i) => (
          <div
            key={i}
            className={cn('space-y-2', multi && 'rounded-xl border border-gray-100 dark:border-gray-800 p-3')}
          >
            {multi && <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{PART_LABELS[i]}</p>}
            {hasHandout && !(multi && forAll) && (
              <div className="space-y-2">
                {multi && (
                  <Toggle
                    checked={Boolean(part.handout)}
                    onChange={on => setPart(i, { handout: on ? part.handout ?? newHandout() : null })}
                  >
                    Bu sualın rekviziti var
                  </Toggle>
                )}
                {part.handout && (
                  <HandoutEditor
                    session={session}
                    value={part.handout}
                    onChange={v => setPart(i, { handout: v })}
                    onError={setError}
                  />
                )}
              </div>
            )}
            <textarea
              rows={4}
              value={part.text}
              onChange={e => setPart(i, { text: e.target.value })}
              placeholder="Sualın mətnini daxil edin"
              aria-label="Sualın mətni"
              className={fieldCls}
            />
            <input
              value={part.answer}
              onChange={e => setPart(i, { answer: e.target.value })}
              placeholder="Sualın cavabı"
              aria-label="Sualın cavabı"
              className={fieldCls}
            />
          </div>
        ))}
      </div>

      <div className="grid gap-3">
        <input
          value={form.considered}
          onChange={e => set('considered', e.target.value)}
          placeholder="Sayılma meyarı"
          aria-label="Sayılma meyarı"
          className={fieldCls}
        />
        <textarea
          rows={3}
          value={form.comment}
          onChange={e => set('comment', e.target.value)}
          placeholder="Sualın şərhi"
          aria-label="Şərh"
          className={fieldCls}
        />
        <textarea
          rows={2}
          value={sourcesText}
          onChange={e => setSourcesText(e.target.value)}
          placeholder="Mənbə (hər sətirdə bir mənbə)"
          aria-label="Mənbələr"
          className={fieldCls}
        />
        <input
          value={form.authors}
          onChange={e => set('authors', e.target.value)}
          placeholder="Müəllif(lər)"
          aria-label="Müəlliflər"
          maxLength={300}
          className={fieldCls}
        />
      </div>

      {error && <ErrorText>{error}</ErrorText>}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onCancel} className={secondaryBtnCls}>Ləğv et</button>
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Saxla
        </button>
      </div>
    </form>
  );
};

export default AppealQuestionForm;
